import React, { useMemo } from 'react';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import pick from 'lodash/pick';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilterProducts,
  FetchProductsFilters,
  ProductCategoryID
} from '../../../../../../products/productsTypes';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../categories/categoriesTypes';
import { IdFilterType } from '../../../../../../../types';
import { IconsEnum } from '../../../../../../../assets/icons/types';
import { CacheFetchProductsAggregationsQueryResponseAggregations } from '../../../../../../products/queries/fetchCacheProductsAggregations.query';

import { AppliedFilterBadge } from './components/AppliedFilterBadge';
import { ProductsCategoriesFilterBadge } from './components/ProductsCategoriesFilterBadge';
import { ProductsColorsFilterBadge } from './components/ProductsColorsFilterBadge';
import { ProductsBrandsFilterBadge } from './components/ProductsBrandsFilterBadge';
import { ProductsCollectionsFilterBadge } from './components/ProductsCollectionsFilterBadge';
import { ProductsTypesFilterBadge } from '../../../../../../products/components/filterBadges/ProductsTypesFilterBadge';
import { ProductsSeasonalFilterBadge } from '../../../../../../products/components/filterBadges/ProductsSeasonalFilterBadge';

import { BooleanFilterBadge } from '../../../../../../../helpers/filterBadges/BooleanFilterBadge';
import { ProductsRenderTypesFilterBadge } from '../../../../../../products/components/filterBadges/ProductsRenderTypesFilterBadge';

import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { isEveryPropEmpty } from '../../../../../../../utils/isEveryPropEmpty';

import { words } from '../../../../../../../locales/keys';

const aiFilterFields = [
  FetchProductsFilterFields.AI_MATERIAL_TAGS,
  FetchProductsFilterFields.AI_INTERIOR_STYLE_TAGS,
  FetchProductsFilterFields.AI_ROOM_CLASSIFICATION_TAGS,
  FetchProductsFilterFields.AI_FEATURE_TAGS
];

const allFilterFields = [
  ...aiFilterFields,
  FetchProductsFilterFields.AI_MAIN_COLOR_TAGS,
  FetchProductsFilterFields.BLOCKED,
  FetchProductsFilterFields.RENDER_TYPE_IDS,
  FetchProductsFilterFields.FAVORITE,
  FetchProductsFilterFields.CURRENT_TEAM_FAVORITE,
  FetchProductsFilterFields.TEAM_ID_FAVORITE,
  FetchProductsFilterFields.SEASONAL_ID,
  FetchProductsFilterFields.PRODUCT_TYPE_ID,
  FetchProductsFilterFields.BRAND_ID,
  FetchProductsFilterFields.COLLECTION_ID
];

type AppliedFiltersType = {
  filterField: FetchProductsFilterFields;
  value: string[];
}[];

interface ThreeDStockProductsFiltersAppliedFiltersProps {
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
  filterProducts: FetchProductsFilterProducts;
  clearFilters?: () => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  defaultProductCategoryId?: IdFilterType;
  defaultProductParentCategoryIds?: ProductCategoryID[];
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations;
}

function ThreeDStockProductsFiltersAppliedFilters({
  productsFilters,
  changeProductsFilters,
  filterProducts,
  clearFilters,
  checkedCategories,
  setCheckedCategories,
  defaultProductCategoryId,
  defaultProductParentCategoryIds,
  productsAggregations
}: ThreeDStockProductsFiltersAppliedFiltersProps) {
  const appliedFilters = useMemo<AppliedFiltersType>(() => {
    const filters: AppliedFiltersType = [];

    forEach(aiFilterFields, (filterField) => {
      if (!isEmpty(productsFilters[filterField])) {
        filters.push({
          filterField,
          value: productsFilters[filterField]?.in || []
        });
      }
    });

    return filters;
  }, [productsFilters]);

  const withFilters =
    !isEveryPropEmpty(pick(productsFilters, allFilterFields)) ||
    !isEmpty(checkedCategories);

  if (!withFilters) {
    return null;
  }

  return (
    <div className="p-4" id="applied-filters">
      <div className="flex flex-wrap -m-0.5">
        {map(appliedFilters, (appliedFilter) => (
          <AppliedFilterBadge
            key={appliedFilter.filterField}
            changeProductsFilters={changeProductsFilters}
            fieldName={appliedFilter.filterField}
            value={appliedFilter.value}
          />
        ))}

        <ProductsColorsFilterBadge
          changeProductsFilters={changeProductsFilters}
          productsFilters={productsFilters}
        />

        <ProductsCollectionsFilterBadge
          changeProductsFilters={changeProductsFilters}
          productsFilters={productsFilters}
          productsAggregations={productsAggregations}
        />

        <ProductsBrandsFilterBadge
          changeProductsFilters={changeProductsFilters}
          productsFilters={productsFilters}
        />

        <ProductsCategoriesFilterBadge
          changeProductsFilters={changeProductsFilters}
          setCheckedCategories={setCheckedCategories}
          checkedCategories={checkedCategories}
          defaultProductCategoryId={defaultProductCategoryId}
          defaultProductParentCategoryIds={defaultProductParentCategoryIds}
        />

        <BooleanFilterBadge
          dataGa="products-blocked-filter-badge-clear"
          filters={productsFilters}
          i18nLabel={words.blocked}
          name={FetchProductsFilterFields.BLOCKED}
          onRemoveFilter={filterProducts}
        />

        <BooleanFilterBadge
          dataGa="products-favorites-filter-badge-clear"
          filters={productsFilters}
          i18nLabel={words.favorites}
          name={FetchProductsFilterFields.FAVORITE}
          onRemoveFilter={filterProducts}
        />

        <BooleanFilterBadge
          dataGa="products-current-company-favorite-filter-badge-clear"
          filters={productsFilters}
          i18nLabel={words.companyFavorites}
          name={FetchProductsFilterFields.CURRENT_TEAM_FAVORITE}
          onRemoveFilter={filterProducts}
        />

        <BooleanFilterBadge
          dataGa="products-company-favorite-filter-badge-clear"
          filters={productsFilters}
          i18nLabel={words.companyFavorites}
          name={FetchProductsFilterFields.TEAM_ID_FAVORITE}
          onRemoveFilter={filterProducts}
        />

        <ProductsRenderTypesFilterBadge
          filters={productsFilters}
          name={FetchProductsFilterFields.RENDER_TYPE_IDS}
          onRemoveFilter={filterProducts}
        />

        {withFilters && (
          <PureIconButtonHelper
            dataGa="products-clear-filters-badge"
            className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 m-0.5"
            i18nText={words.clear}
            i18nTextClassName="inline"
            icon={IconsEnum.X}
            iconClassName="w-4 h-4"
            onClick={clearFilters}
          />
        )}

        <ProductsTypesFilterBadge
          filters={productsFilters}
          name={FetchProductsFilterFields.PRODUCT_TYPE_ID}
          onRemoveFilter={filterProducts}
        />

        <ProductsSeasonalFilterBadge
          filters={productsFilters}
          name={FetchProductsFilterFields.SEASONAL_ID}
          onRemoveFilter={filterProducts}
        />
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersAppliedFilters;
